import { useState, useEffect } from "react";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { Textarea } from "@/components/ui/Textarea";
import { Label } from "@/components/ui/Label";
import CryptoJS from "crypto-js";
import { ToolPageLayout } from "@/components/ToolPageLayout";
import { Copy, RotateCcw } from "lucide-react";
import { copyToClipboard } from "@/lib/utils";

export function PasswordStrengthChecker() {
  const [password, setPassword] = useState("S3cur3_Pa$$w0rd!_99");

  let score = 0;
  if (password.length > 8) score++;
  if (password.length > 12) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[a-z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;

  const strength = ["Very Weak", "Weak", "Fair", "Good", "Strong", "Very Strong"][Math.min(5, score)] || "Very Weak";
  const color = ["bg-red-500", "bg-orange-500", "bg-yellow-500", "bg-green-400", "bg-green-600", "bg-green-800"][Math.min(5, score)] || "bg-red-500";

  return (
    <ToolPageLayout
      title="Password Strength Checker Online - Evaluate Password Security"
      description="Test your password strength in real-time with our free offline evaluation tool. Get detailed diagnostics on complexity and entropy without sending passwords to servers."
      h1="Password Strength Checker"
      category="JWT & Security"
      categoryPath="/jwt-security"
      howToUse={[
        "Type your candidate password into the input area.",
        "Inspect the real-time entropy status bar and strength rating.",
        "Check that length, cases, numbers, and symbols are well balanced."
      ]}
      faq={[
        { question: "What is an online password strength checker and why is it useful?", answer: "An online password strength checker evaluates how resilient a password is to automated hacking attempts. Using one helps you identify weak credentials before you register them on web portals, protecting your accounts from being easily compromised." },
        { question: "How does this password complexity analyzer calculate and score my input?", answer: "Our analyzer grades your password using multi-factor heuristics: rewarding overall character length, and checking for diversity across uppercase letters, lowercase letters, numerical digits, and special symbols (like !, @, #, $)." },
        { question: "Is it safe to test my actual personal passwords on this online analyzer?", answer: "Yes, this tool is 100% secure. It performs all calculations directly inside your local browser memory using client-side JavaScript. No data is transmitted across the internet, making it entirely private and offline-friendly." },
        { question: "What is the absolute minimum length recommended for a secure password?", answer: "A safe password should be at least 12 characters long, though 16 or more characters is highly recommended. Each additional character exponentially multiplies the difficulty of brute-force and dictionary-based hacking systems." },
        { question: "Why is character diversity critical for maximizing password strength?", answer: "Diversity increases the size of the potential character pool (from 26 characters for lowercase only, to 94 characters for full alphanumeric and symbol sets). This significantly increases the total mathematical space a brute-force script must crawl." },
        { question: "What is the difference between password strength and password entropy?", answer: "Password strength is a qualitative rating (e.g., 'Weak' or 'Strong') based on general heuristics. Password entropy is a precise mathematical measurement (in bits) of the credential's randomness, defining exactly how many guesses are required to crack it." },
        { question: "What are the most common security mistakes that make passwords vulnerable?", answer: "Common mistakes include using consecutive numbers (like '12345'), simple keyboard walks (like 'qwerty'), personal details (birthdays, names), and reusing the same prefix or suffix across multiple websites." },
        { question: "How does a brute-force search attempt to crack my password?", answer: "Hackers use powerful GPU clusters that systematically try every possible character permutation. Weak passwords are cracked in milliseconds, while high-entropy, diverse passwords would require hundreds of years of computing power." },
        { question: "Does substituting characters (like 'a' with '@' or 'i' with '1') make a password secure?", answer: "No. Modern dictionary attacks are pre-programmed with common letter-to-symbol substitutions (called 'l33t speak'). Standard substitutions do not provide adequate protection compared to simply increasing the overall length of the password." },
        { question: "How can I easily create a secure, high-scoring password that I can remember?", answer: "A great strategy is to create a 'passphrase' by stringing together four or five completely random, unrelated words (e.g., 'bacon-galaxy-laptop-ocean'). This creates an exceptionally long password with high entropy that remains easy for humans to recall." }
      ]}
    >
      <div className="space-y-4 max-w-xl mx-auto">
        <Label className="text-sm font-semibold text-slate-700">Enter Password to Analyze</Label>
        <Input type="text" value={password} onChange={e => setPassword(e.target.value)} placeholder="Type password here..." />
        <div className="h-2.5 w-full bg-slate-100 rounded-full overflow-hidden mt-4">
          <div className={`h-full transition-all duration-300 ${color}`} style={{ width: `${(Math.min(5, score) / 5) * 100}%` }} />
        </div>
        <div className="text-center mt-3">
          <span className="text-xs text-slate-500 uppercase tracking-wider block">Strength Rating</span>
          <span className="font-bold text-lg text-slate-800">{password ? strength : "Type a password"}</span>
        </div>
      </div>
    </ToolPageLayout>
  );
}

const SAMPLE_HASH_1 = "e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855";
const SAMPLE_HASH_2 = "E3B0C44298FC1C149AFBF4C8996FB92427AE41E4649B934CA495991B7852B855";

export function HashComparator() {
  const [hash1, setHash1] = useState(SAMPLE_HASH_1);
  const [hash2, setHash2] = useState(SAMPLE_HASH_2);

  const match = hash1 && hash2 && hash1.trim() === hash2.trim();
  const sameCaseMatch = hash1 && hash2 && hash1.trim().toLowerCase() === hash2.trim().toLowerCase();

  return (
    <ToolPageLayout
      title="Hash Comparator Online - Compare Checksums"
      description="Compare and match two hash checksums or digital fingerprints side-by-side. Supports case-insensitive matching for MD5, SHA1, and SHA256."
      h1="Hash Comparator"
      category="JWT & Security"
      categoryPath="/jwt-security"
      howToUse={[
        "Paste the first hash checksum into the left area.",
        "Paste the second hash checksum into the right area.",
        "The tool instantly assesses matches and handles case differences."
      ]}
      faq={[
        { question: "What is an online hash comparator and why is it used?", answer: "An online hash comparator is a tool that allows you to compare two cryptographic hashes side-by-side. It is commonly used by developers and security professionals to verify downloaded file integrity and guarantee that files have not been modified or corrupted." },
        { question: "What is a cryptographic hash checksum or digital fingerprint?", answer: "A hash checksum is a fixed-length string produced by a hashing algorithm (such as SHA-256 or MD5) that uniquely represents the contents of a digital file or string of text. Even a single character change in the file yields a completely different hash." },
        { question: "How does a hash comparison verify that a file hasn't been tampered with?", answer: "When software developers release software, they publish a secure, authentic checksum of the installer file. By generating a checksum of your downloaded copy and comparing it with the published hash, you can confirm they match exactly, proving no tampering has occurred." },
        { question: "Is this hash comparison tool case-sensitive?", answer: "Yes and no. The tool analyzes both. It reports a direct 'Exact Match' if the character casing matches perfectly, and it flags a 'Case-Insensitive Match' if the underlying hexadecimal values match but use different uppercase or lowercase letters." },
        { question: "What are the key differences between MD5, SHA-1, and SHA-256?", answer: "MD5 (128-bit) and SHA-1 (160-bit) are older, faster algorithms that are now considered cryptographically broken due to collision vulnerabilities. SHA-256 (256-bit) and SHA-512 (512-bit) are modern, highly secure algorithms used for industrial-grade security." },
        { question: "What is a cryptographic hash collision and is it dangerous?", answer: "A hash collision happens when two distinct inputs yield the exact same hash checksum. While mathematically possible, modern algorithms like SHA-256 make collisions virtually impossible. Collisions are dangerous because they could allow attackers to forge malicious files that bypass checks." },
        { question: "Can a hash checksum be decrypted or reversed to recover the original file?", answer: "No. Cryptographic hashing functions are strictly one-way mathematical operations. It is computationally impossible to reverse-engineer or reconstruct the original text or file from its hash digest value." },
        { question: "Why does my file's hash checksum change when I copy or rename it?", answer: "Renaming or moving a file does not alter its internal bytes, so its hash remains identical. However, if the file's internal data, encoding, hidden metadata, or line-ending format (such as CRLF vs LF) changes, the hash will change completely." },
        { question: "Are my sensitive checksums secure when pasted into this online comparator?", answer: "Yes, this tool is 100% secure. All matching and comparison logic is executed locally inside your browser sandbox using client-side JavaScript. No data or pasted checksums are ever transmitted over networks to external servers." },
        { question: "How can I easily generate a file checksum on Windows, macOS, or Linux?", answer: "On Windows, use PowerShell: 'Get-FileHash filepath -Algorithm SHA256'. On macOS, use Terminal: 'shasum -a 256 filepath'. On Linux, run: 'sha256sum filepath' to quickly find your file's checksum." }
      ]}
    >
      <div className="space-y-6">
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-sm font-semibold text-slate-700">Hash 1</Label>
            <Textarea value={hash1} onChange={e => setHash1(e.target.value)} className="font-mono text-xs min-h-[100px]" placeholder="Paste first checksum..." />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-semibold text-slate-700">Hash 2</Label>
            <Textarea value={hash2} onChange={e => setHash2(e.target.value)} className="font-mono text-xs min-h-[100px]" placeholder="Paste second checksum..." />
          </div>
        </div>
        {hash1 && hash2 ? (
          <div className={`p-6 rounded-2xl text-center font-bold text-lg border ${match ? "bg-emerald-50 text-emerald-800 border-emerald-100" : sameCaseMatch ? "bg-yellow-50 text-yellow-800 border-yellow-100" : "bg-red-50 text-red-800 border-red-100"}`}>
            {match ? "Exact Match!" : sameCaseMatch ? "Case-Insensitive Match (Same values, different cases)" : "Checksums Do Not Match"}
          </div>
        ) : (
          <div className="p-4 bg-slate-50 text-slate-400 text-center rounded-xl text-sm font-medium">
            Paste checksums above to trigger automatic comparison
          </div>
        )}
      </div>
    </ToolPageLayout>
  );
}

export function HmacGenerator() {
  const [text, setText] = useState("Hello, securely authenticated API endpoint!");
  const [secret, setSecret] = useState("super_secret_shared_key_123");
  const [algo, setAlgo] = useState("SHA256");
  const [output, setOutput] = useState("");
  const [copied, setCopied] = useState(false);

  const generate = () => {
    if (!text || !secret) {
      setOutput("");
      return;
    }
    let hash = "";
    switch (algo) {
      case "MD5": hash = CryptoJS.HmacMD5(text, secret).toString(); break;
      case "SHA1": hash = CryptoJS.HmacSHA1(text, secret).toString(); break;
      case "SHA256": hash = CryptoJS.HmacSHA256(text, secret).toString(); break;
      case "SHA512": hash = CryptoJS.HmacSHA512(text, secret).toString(); break;
    }
    setOutput(hash);
  };

  useEffect(() => {
    generate();
  }, [text, secret, algo]);

  const handleCopy = () => {
    copyToClipboard(output).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <ToolPageLayout
      title="HMAC Generator Online - Hash-based Message Authentication Codes"
      description="Generate standard Hash-based Message Authentication Codes (HMAC) with MD5, SHA-1, SHA-256, or SHA-512 instantly in-browser."
      h1="HMAC Generator"
      category="JWT & Security"
      categoryPath="/jwt-security"
      howToUse={[
        "Type your message data payload in the left message text block.",
        "Enter your cryptographic secret shared key.",
        "Select your hash algorithm (SHA256 recommended).",
        "Copy the generated hex-encoded HMAC checksum."
      ]}
      faq={[
        { question: "What is an online HMAC generator and why is it used?", answer: "An online HMAC generator is a security tool that creates a Hash-based Message Authentication Code. Developers use it to verify data authenticity and data integrity by signing messages with a cryptographic hash function in combination with a shared secret key." },
        { question: "What does HMAC stand for and what is its primary purpose?", answer: "HMAC stands for Hash-based Message Authentication Code. Its primary purpose is to prove that a received message has not been altered in transit and that the sender possesses the identical shared secret key, establishing reliable sender authenticity." },
        { question: "What is the key difference between a standard Hash and an HMAC?", answer: "A standard Hash (e.g., SHA-256) takes only an input message and returns a digest, making it vulnerable to length extension attacks. An HMAC takes both an input message and a secret key, wrapping the hash process in double layers of encryption to prevent forging." },
        { question: "How does HMAC utilize the shared secret key in calculation?", answer: "The HMAC algorithm hashes the secret key first, blends it with the input message data, hashes that combination, and then hashes the result again with another padded variation of the secret key, ensuring maximum cryptographic strength." },
        { question: "Why is HMAC SHA-256 widely preferred for REST APIs and webhook security?", answer: "HMAC SHA-256 strikes a perfect balance between high speed and top-tier cryptographic security. It allows external platforms (like Stripe, GitHub, or Shopify) to sign transaction webhooks so recipient servers can confirm requests originated from authentic sources." },
        { question: "Can a hacker reverse-engineer or decode an HMAC to retrieve the shared secret key?", answer: "No. Cryptographic hash functions are strictly one-way. An attacker cannot decrypt or reverse-engineer an HMAC signature to find the original secret key, making brute-force dictionary attacks the only option, which fails when strong keys are used." },
        { question: "Are my private message payloads and secret keys safe on this online tool?", answer: "Yes, our HMAC Generator runs entirely locally inside your browser memory using client-side JavaScript. No keys, messages, or generated signatures are ever sent to external databases or servers, ensuring full offline data confidentiality." },
        { question: "What is the difference between HMAC-SHA256 and HMAC-SHA512?", answer: "HMAC-SHA256 uses the 256-bit SHA-2 hash algorithm and produces a 64-character hexadecimal signature. HMAC-SHA512 uses the larger 512-bit SHA-2 hash algorithm, producing a 128-character hexadecimal signature with higher collision resistance." },
        { question: "Does changing the length of the secret key affect the length of the HMAC output?", answer: "No. Regardless of how short or long your secret key is, the generated HMAC's output length is determined solely by the selected hashing algorithm. For example, HMAC-SHA256 will always output a fixed 64-character hex string." },
        { question: "How does a server verify an incoming webhook HMAC signature?", answer: "1. The server receives the request body and the signature header. 2. The server generates its own HMAC of the received body using the stored secret key. 3. The server compares its generated signature with the received signature. If they match, the request is processed." }
      ]}
    >
      <div className="space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-sm font-semibold text-slate-700">Message Content</Label>
            <Textarea value={text} onChange={e => setText(e.target.value)} placeholder="Type message data..." className="min-h-[160px]" />
          </div>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-slate-700">Cryptographic Secret Key</Label>
              <Input type="text" value={secret} onChange={e => setSecret(e.target.value)} placeholder="Enter secret key..." />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-slate-700">Hash Algorithm</Label>
              <select value={algo} onChange={e => setAlgo(e.target.value)} className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm">
                <option>MD5</option>
                <option>SHA1</option>
                <option>SHA256</option>
                <option>SHA512</option>
              </select>
            </div>
          </div>
        </div>

        {output && (
          <div className="space-y-2 pt-4">
            <div className="flex justify-between items-center">
              <Label className="text-xs font-semibold text-slate-500 uppercase">Generated HMAC Hash</Label>
              <Button variant="outline" size="sm" onClick={handleCopy} className="text-xs gap-1 py-1 h-7">
                <Copy className="w-3.5 h-3.5" />
                {copied ? "Copied!" : "Copy Hash"}
              </Button>
            </div>
            <div className="p-4 bg-slate-50 border rounded-xl font-mono text-center break-all text-xs text-slate-700">
              {output}
            </div>
          </div>
        )}
      </div>
    </ToolPageLayout>
  );
}

export function RandomStringGenerator() {
  const [length, setLength] = useState(32);
  const [count, setCount] = useState(5);
  const [chars, setChars] = useState("ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*");
  const [output, setOutput] = useState("");
  const [copied, setCopied] = useState(false);

  const generate = () => {
    if (!chars) return;
    let res = [];
    for (let i = 0; i < count; i++) {
      let str = "";
      for (let j = 0; j < length; j++) {
        str += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      res.push(str);
    }
    setOutput(res.join("\n"));
  };

  useEffect(() => {
    generate();
  }, []);

  const handleCopy = () => {
    copyToClipboard(output).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <ToolPageLayout
      title="Random String Generator Online - Generate Secure Tokens"
      description="Create secure random strings, API keys, session secrets, or password salts instantly with custom length and allowed character selections."
      h1="Random String Generator"
      category="Security & Crypto"
      categoryPath="/security-crypto"
      howToUse={[
        "Configure your desired string character length and quantity to produce.",
        "Review or edit the set of allowed characters (add special characters if needed).",
        "Click Generate to compute cryptographically random strings instantly in memory."
      ]}
      faq={[
        { question: "Where is this generator useful?", answer: "Perfect for creating secure database salt values, unique API keys, temporary random passwords, or mock values." },
        { question: "Is this cryptographically secure?", answer: "It generates pseudorandom characters cleanly inside the client's local runtime." }
      ]}
    >
      <div className="space-y-4">
        <div className="grid md:grid-cols-3 gap-4 items-end">
          <div className="space-y-2">
            <Label className="text-sm font-semibold text-slate-700">String Length</Label>
            <Input type="number" value={length} onChange={e => setLength(Number(e.target.value) || 32)} />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-semibold text-slate-700">Quantity (Lines)</Label>
            <Input type="number" value={count} onChange={e => setCount(Number(e.target.value) || 5)} />
          </div>
          <Button onClick={generate} className="bg-slate-900 hover:bg-slate-800 text-white font-medium h-10">Generate Strings</Button>
        </div>
        <div className="space-y-2">
          <Label className="text-sm font-semibold text-slate-700">Characters Allowed</Label>
          <Input type="text" value={chars} onChange={e => setChars(e.target.value)} />
        </div>
        
        {output && (
          <div className="space-y-2 pt-2">
            <div className="flex justify-between items-center">
              <Label className="text-xs font-semibold text-slate-500 uppercase">Generated Output</Label>
              <Button variant="outline" size="sm" onClick={handleCopy} className="text-xs gap-1 py-1 h-7">
                <Copy className="w-3.5 h-3.5" />
                {copied ? "Copied!" : "Copy All"}
              </Button>
            </div>
            <Textarea value={output} readOnly className="min-h-[160px] font-mono bg-slate-50 text-xs text-slate-700 leading-relaxed" />
          </div>
        )}
      </div>
    </ToolPageLayout>
  );
}
